import type { Recurrence, Schedule } from '../storage/schemas';
import {
  dayNumber,
  daysInMonth,
  fromDayNumber,
  isValidTimeZone,
  localDateOf,
  parseDate,
  parseTime,
  wallToInstant,
  weekday,
  type LocalDate,
} from './tz';

function matchesDate(rule: Recurrence, date: LocalDate): boolean {
  const start = parseDate(rule.startDate);
  const dn = dayNumber(date);
  const sn = dayNumber(start);
  if (rule.kind === 'once') return dn === dayNumber(parseDate(rule.date));
  if (dn < sn) return false;
  if (rule.endDate && dn > dayNumber(parseDate(rule.endDate))) return false;
  switch (rule.kind) {
    case 'daily':
      return (dn - sn) % rule.interval === 0;
    case 'weekly': {
      const days = rule.weekdays.length ? rule.weekdays : [weekday(start)];
      if (!days.includes(weekday(date))) return false;
      // Weeks start on Sunday; count whole weeks between the Sundays on/before each date.
      const startWeek = sn - weekday(start);
      const thisWeek = dn - weekday(date);
      return ((thisWeek - startWeek) / 7) % rule.interval === 0;
    }
    case 'monthly': {
      const months = (date.y - start.y) * 12 + (date.m - start.m);
      if (months < 0 || months % rule.interval !== 0) return false;
      // 29–31 clamp to the last day of shorter months.
      return date.d === Math.min(rule.monthDay, daysInMonth(date.y, date.m));
    }
  }
}

function instantFor(rule: Recurrence, date: LocalDate): number {
  const { hh, mm } = parseTime(rule.time);
  return wallToInstant({ ...date, hh, mm }, rule.timeZone);
}

/** All occurrence instants with from <= t < to, ascending. */
export function occurrencesBetween(rule: Recurrence, from: number, to: number, limit = 2000): number[] {
  if (to <= from || !isValidTimeZone(rule.timeZone)) return [];
  if (rule.kind === 'once') {
    const t = instantFor(rule, parseDate(rule.date));
    return t >= from && t < to ? [t] : [];
  }
  let first = dayNumber(localDateOf(from, rule.timeZone)) - 1;
  let last = dayNumber(localDateOf(to, rule.timeZone)) + 1;
  first = Math.max(first, dayNumber(parseDate(rule.startDate)));
  if (rule.endDate) last = Math.min(last, dayNumber(parseDate(rule.endDate)));
  const out: number[] = [];
  for (let n = first; n <= last && out.length < limit; n++) {
    const date = fromDayNumber(n);
    if (!matchesDate(rule, date)) continue;
    const t = instantFor(rule, date);
    if (t >= from && t < to) out.push(t);
  }
  return out;
}

const WINDOWS = [2, 40, 400, 4000, 12000].map((d) => d * 864e5);

/** First occurrence strictly after `after`, or null if the rule has ended. */
export function nextOccurrence(rule: Recurrence, after: number): number | null {
  for (const w of WINDOWS) {
    const hit = occurrencesBetween(rule, after + 1, after + 1 + w, 1);
    if (hit.length) return hit[0];
    if (rule.kind === 'once') return null;
    if (rule.endDate && dayNumber(parseDate(rule.endDate)) < dayNumber(localDateOf(after, rule.timeZone))) return null;
  }
  return null;
}

/** Most recent occurrence at or before `at`, or null. */
export function previousOccurrence(rule: Recurrence, at: number): number | null {
  for (const w of WINDOWS) {
    const hits = occurrencesBetween(rule, at - w, at + 1);
    if (hits.length) return hits[hits.length - 1];
    if (dayNumber(parseDate(rule.kind === 'once' ? rule.date : rule.startDate)) > dayNumber(localDateOf(at, rule.timeZone))) return null;
  }
  return null;
}

export function nextOccurrences(rule: Recurrence, after: number, count: number): number[] {
  const out: number[] = [];
  let cursor = after;
  while (out.length < count) {
    const n = nextOccurrence(rule, cursor);
    if (n === null) break;
    out.push(n);
    cursor = n;
  }
  return out;
}

// ---------------------------------------------------------------- validation

export interface ScheduleIssue {
  level: 'error' | 'warning';
  field: string;
  message: string;
}

export function validateSchedule(s: Schedule, others: Schedule[], now = Date.now()): ScheduleIssue[] {
  const issues: ScheduleIssue[] = [];
  const r = s.rule;
  if (!isValidTimeZone(r.timeZone)) issues.push({ level: 'error', field: 'timeZone', message: 'Unknown time zone.' });
  const validDate = (v: string) => {
    const d = parseDate(v);
    return d.m >= 1 && d.m <= 12 && d.d >= 1 && d.d <= daysInMonth(d.y, d.m);
  };
  if (r.kind === 'once' && !validDate(r.date)) issues.push({ level: 'error', field: 'date', message: 'That date does not exist.' });
  if (!validDate(r.startDate)) issues.push({ level: 'error', field: 'startDate', message: 'Start date does not exist.' });
  if (r.endDate && !validDate(r.endDate)) issues.push({ level: 'error', field: 'endDate', message: 'End date does not exist.' });
  if (r.kind !== 'once' && r.endDate && r.endDate < r.startDate) issues.push({ level: 'error', field: 'endDate', message: 'End date is before the start date.' });
  if (r.kind === 'weekly' && r.weekdays.length === 0) issues.push({ level: 'error', field: 'weekdays', message: 'Pick at least one day.' });
  if (!s.notify && !s.switchNote) issues.push({ level: 'error', field: 'notify', message: 'Turn on a reminder, a note switch, or both.' });
  if (issues.some((i) => i.level === 'error')) return issues;

  if (r.kind === 'once' && isValidTimeZone(r.timeZone)) {
    const t = occurrencesBetween(r, 0, Number.MAX_SAFE_INTEGER)[0];
    if (t !== undefined && t <= now) issues.push({ level: 'error', field: 'date', message: 'That time has already passed.' });
  }
  if (r.kind === 'monthly' && r.monthDay > 28) issues.push({ level: 'warning', field: 'monthDay', message: `In shorter months this runs on the last day.` });
  if (r.kind !== 'once' && nextOccurrence(r, now) === null) issues.push({ level: 'error', field: 'endDate', message: 'This schedule has no future occurrences.' });

  const same = others.find(
    (o) =>
      o.id !== s.id &&
      o.noteId === s.noteId &&
      o.enabled &&
      JSON.stringify({ ...o.rule, weekdays: [...o.rule.weekdays].sort() }) === JSON.stringify({ ...r, weekdays: [...r.weekdays].sort() }),
  );
  if (same) issues.push({ level: 'error', field: 'rule', message: 'An identical schedule for this note already exists.' });

  if (s.switchNote && s.enabled) {
    const next = nextOccurrences(r, now, 8);
    const clash = others.find((o) => o.id !== s.id && o.enabled && o.switchNote && o.noteId !== s.noteId && nextOccurrences(o.rule, now, 8).some((t) => next.includes(t)));
    if (clash) issues.push({ level: 'warning', field: 'time', message: `Another schedule switches to a different note at the same time ("${clash.title || 'untitled'}"). The one created later wins.` });
  }
  return issues;
}

export function describeRule(r: Recurrence): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const every = (n: number, unit: string) => (n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`);
  switch (r.kind) {
    case 'once':
      return `Once on ${r.date} at ${r.time}`;
    case 'daily':
      return `${every(r.interval, 'day')} at ${r.time}`;
    case 'weekly': {
      const sorted = [...r.weekdays].sort();
      const label =
        sorted.join() === '1,2,3,4,5' ? 'weekdays' : sorted.join() === '0,6' ? 'weekends' : sorted.length === 7 ? 'every day' : sorted.map((d) => days[d]).join(', ');
      return `${r.interval === 1 ? 'Weekly' : `Every ${r.interval} weeks`} on ${label} at ${r.time}`;
    }
    case 'monthly':
      return `${every(r.interval, 'month')} on day ${r.monthDay} at ${r.time}`;
  }
}
